import { useState } from 'react';
// GAP: no DS icon for an image / uploaded signature (ImageIcon); lucide in both modes (design sheet §3.1, logged not substituted).
import { ImageIcon, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad, Segmented, Modal } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import {
  useUploadImageFile,
  readFileAsBase64,
  type ImageArea,
} from '@/services/common/use-upload-image';
import {
  validateImageFile,
  DEFAULT_IMAGE_ACCEPT,
  DEFAULT_IMAGE_MAX_SIZE_MB,
} from '@/shared/lib/validate-image-file';

/**
 * SignatureCaptureField — settings capture control for a provider signature
 * (or any single ink/stamp asset). Composes the Signal DS `SignaturePad`
 * (draw) with a file picker (upload) so the provider can EITHER draw their
 * signature OR upload an image of it (Samer-confirmed "both" on PROD-1051).
 *
 * Storage contract (parity with `practice-details-tab.tsx`'s stamp/image
 * flow + Flutter `doctor_signature_widget.dart` / `practice_stamp_widget.dart`):
 *   - The captured PNG (drawn data-URL) or picked file is base64'd and POSTed
 *     to `/api/Master/UploadImage` via `useUploadImage`, returning a stored
 *     CDN/blob URL.
 *   - Flutter uploaded BOTH the doctor signature and the practice stamp under
 *     area `"PRACTICE"` (doctor_signature_widget.dart:166 = `imageAreaPractice`),
 *     so `area` defaults to `PRACTICE` to match the live app verbatim
 *     (magic-strings rule, CLAUDE.md → Flutter → React Conversion Contract).
 *   - Parent owns the persisted URL: `onChange(url)` writes it back into the
 *     draft, and the parent's existing save mutation round-trips it
 *     (`doctorSignatureUrl` on ProfessionalDetails / `practiceStampUrl` on the
 *     practice). On a dedup re-upload the backend returns an empty body
 *     (`undefined`); we keep the existing value and toast success in that case
 *     (same contract as the practice image/stamp slots).
 *
 * The base64 payload lives only in local component state for the duration of
 * the capture dialog and is dropped on close — never logged, never persisted
 * (the upload hook also zeroes its mutation gcTime for the same reason).
 */

type CaptureMode = 'draw' | 'upload';

const MODE_OPTIONS = [
  { value: 'draw', label: 'Draw' },
  { value: 'upload', label: 'Upload' },
] satisfies { value: CaptureMode; label: string }[];

export interface SignatureCaptureFieldProps {
  /** Caption above the tile. Doubles as the accessible name. */
  label: string;
  /** Current stored image URL, or null when unset. */
  value: string | null;
  /** Fires with the newly stored URL after upload, or null on remove. */
  onChange: (url: string | null) => void;
  /** Upload area tag. Defaults to `PRACTICE` (Flutter parity — see header). */
  area?: ImageArea;
  /** Read-only — preview only, no capture/remove affordances. */
  readOnly?: boolean;
  className?: string;
}

export function SignatureCaptureField({
  label,
  value,
  onChange,
  area = 'PRACTICE',
  readOnly = false,
  className,
}: SignatureCaptureFieldProps) {
  const uploadImage = useUploadImageFile();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<CaptureMode>('draw');
  // The in-flight capture as a PNG/image data-URL — from the pad (draw) or the
  // picked file (upload). Held locally; dropped when the dialog closes.
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPending(null);
    setError(null);
    setMode('draw');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleModeChange = (next: string) => {
    setMode(next as CaptureMode);
    setPending(null);
    setError(null);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Type allowlist (PNG/JPEG/HEIC) + size cap. A signature image is tiny, so
    // cap at 2 MB (tighter than the app-wide 10 MB default) — this is
    // PHI-adjacent blob storage, so keep the unvalidated-upload surface small.
    const validationError = validateImageFile(file, { maxSizeMB: 2 });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setPending(await readFileAsBase64(file));
  };

  const handleSave = async () => {
    if (!pending) return;
    try {
      // `pending` is a PNG data-URL (from the pad or the picked file). Convert
      // it to a Blob for the multipart upload — fetch() on a data: URL is
      // synchronous-ish and never hits the network / CORS.
      const blob = await (await fetch(pending)).blob();
      const url = await uploadImage.mutateAsync({ blob, area });
      if (url) {
        onChange(url);
        handleOpenChange(false);
      } else {
        // The multipart endpoint returns the URL on success or throws on
        // failure, so an empty string here is unexpected — surface it rather
        // than closing with a false success (which would leave the parent's
        // Save disabled, since `doctorSignatureUrl` never changed).
        toast.error(
          'Could not save the signature — the image service returned no URL. Please try again.',
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload signature.',
      );
    }
  };

  const handleRemove = () => {
    onChange(null);
    handleOpenChange(false);
  };

  return (
    <div data-slot="signature-field" className={cn('inline-block', className)}>
      <span data-slot="signature-label" className="mb-1 block text-sm font-medium text-text-secondary">
        {label}
      </span>

      {value ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Preview ${label}`}
          data-slot="signature-tile"
          data-state="filled"
          className="relative flex h-20 w-40 items-center justify-center overflow-hidden rounded-md border border-border-default bg-surface-card p-1 transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
        >
          <img src={value} alt={label} className="max-h-full max-w-full object-contain" />
        </button>
      ) : readOnly ? (
        <div
          aria-label={`${label} (read-only)`}
          data-slot="signature-tile"
          data-state="readonly"
          className="flex h-20 w-40 items-center justify-center rounded-md border border-dashed border-border-default bg-surface-card text-text-secondary"
        >
          <ImageIcon className="size-5" aria-hidden />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Add ${label}`}
          data-slot="signature-tile"
          data-state="empty"
          className="flex h-20 w-40 items-center justify-center gap-2 rounded-md border border-dashed border-border-default bg-surface-card text-text-secondary transition hover:border-blue-500 hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
        >
          <PenLine className="size-4" aria-hidden />
          <span className="text-xs">Add signature</span>
        </button>
      )}

      <Modal
      open={open}
      onClose={() => handleOpenChange(false)}
      title={label}
      size="md"
      footer={<><div
              className={cn(
                'flex w-full items-center',
                value && !readOnly ? 'justify-between' : 'justify-end',
              )}
            >
              {value && !readOnly && (
                <Button type="button" variant="tertiary" onClick={handleRemove}>
                  Remove
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={() => handleOpenChange(false)}
                >
                  {readOnly ? 'Close' : 'Cancel'}
                </Button>
                {!readOnly && (
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={!pending || uploadImage.isPending}
                  >
                    {uploadImage.isPending ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </div>
            </div></>}
    >
          

          {readOnly ? (
            <div data-slot="signature-preview" className="flex items-center justify-center rounded-lg bg-grey-100 p-3">
              {value ? (
                <img src={value} alt={label} className="max-h-signature-preview max-w-full object-contain" />
              ) : (
                <ImageIcon className="size-6 text-text-secondary" aria-hidden />
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Segmented
                aria-label="Capture method"
                value={mode}
                onChange={handleModeChange}
                options={MODE_OPTIONS}
              />

              {mode === 'draw' ? (
                <SignaturePad
                  label="Draw your signature"
                  height={190}
                  value={pending}
                  onChange={setPending}
                  clearable
                />
              ) : (
                <div className="space-y-2">
                  <label data-slot="signature-drop" className="flex h-47.5 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-default bg-grey-100 text-text-secondary transition hover:border-blue-500 hover:text-blue-500">
                    {pending ? (
                      <img src={pending} alt="Selected signature" className="max-h-37.5 max-w-full object-contain" />
                    ) : (
                      <>
                        <ImageIcon className="size-6" aria-hidden />
                        <span data-slot="signature-drop-title" className="text-sm">Click to choose an image</span>
                        <span data-slot="signature-drop-hint" className="text-xs">PNG / JPEG, max {DEFAULT_IMAGE_MAX_SIZE_MB} MB</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept={DEFAULT_IMAGE_ACCEPT}
                      onChange={handleFile}
                      className="sr-only"
                    />
                  </label>
                </div>
              )}

              {error && (
                <p role="alert" data-slot="signature-error" className="text-sm text-status-error">
                  {error}
                </p>
              )}
            </div>
          )}

          
        </Modal>
    </div>
  );
}
